import {
  PayloadAction,
  SerializedError,
  createAsyncThunk,
  createSlice,
} from "@reduxjs/toolkit";
import {
  deleteUserThread,
  fetchConversations,
} from "@/services/conversationService";

import { ControlState } from "@/types/control";
import { Conversation } from "@/types/conversation";
import { RootState } from "@/redux/store";
import { getCurrentFormattedDate } from "@/utils/time";
import { KEYS_USERNAME } from "@/constants/message";
import { normalizeGroupAddress } from "@/services/groupKeyService";

/**
 * A conversation's entry in keyExchangeVersions (see ControlState). Canonicalizes group
 * addresses; a no-op for 1:1 usernames. Single source for the map's keying convention —
 * used by both the cache-retry gate (chatSlice) and the key-ready-reload gate (ChatInputs).
 */
export const keyVersionForAddress = (versions: Record<string, number>, address: string): number =>
  versions[normalizeGroupAddress(address)] ?? 0;

const initialState: ControlState = {
  conversations: [],
  currConversationIndex: null,
  loading: false,
  error: "",
  keyExchangeVersion: 0,
  keyExchangeVersions: {},
  encryptionEnabled: true,
  groupEncryptionEnabled: true,
  conversationEncryptionReady: false,
};

export const controlSlice = createSlice({
  name: "control",
  initialState,
  reducers: {
    setCurrConversation(
      state: ControlState,
      action: PayloadAction<number | null>
    ) {
      if (action.payload !== null) {
        state.conversations[action.payload].unread = false;
      }
      state.currConversationIndex = action.payload;
      state.conversationEncryptionReady = false;
    },
    markConversationUnread(state: ControlState, action: PayloadAction<number>) {
      const conv = state.conversations[action.payload];
      if (conv) conv.unread = true;
    },
    addConversation(state: ControlState, action: PayloadAction<Conversation>) {
      state.conversations.unshift({
        type: action.payload.type,
        fullname: action.payload.fullname,
        address: action.payload.address,
        addressId: action.payload.addressId,
        addressType: action.payload.addressType,
        groupName: action.payload.groupName,
        ownerName: action.payload.ownerName,
        updated: action.payload.updated,
      });
      state.currConversationIndex = 0;
    },
    moveCurrConversationToTop(state) {
      const currIndex = state.currConversationIndex;
      if (currIndex === null || currIndex === 0) return; // don't move

      // update time
      const conversation = state.conversations[currIndex];
      conversation.updated = getCurrentFormattedDate();

      // move conversation to top
      state.conversations.splice(currIndex, 1);
      state.conversations.unshift(conversation);
      state.currConversationIndex = 0;
    },
    bumpConversation(
      state,
      action: PayloadAction<{ address: string; addressType: number }>
    ) {
      const { address, addressType } = action.payload;

      // Capture the current conversation's stable identity before mutating anything,
      // so we can re-resolve currConversationIndex after the sort.
      const currAddr =
        state.currConversationIndex !== null
          ? state.conversations[state.currConversationIndex]?.address ?? null
          : null;
      const currAddrType =
        state.currConversationIndex !== null
          ? state.conversations[state.currConversationIndex]?.addressType ?? null
          : null;

      // Stamp the bumped conversation's timestamp.
      // address is always conv.address from the Redux store, so direct equality is correct
      // for both user and group conversations — no JSON parsing needed.
      const target = state.conversations.find(
        (c: Conversation) => c.address === address && c.addressType === addressType
      );
      if (!target) return;
      target.updated = getCurrentFormattedDate();

      // Sort newest-first. The updated field is a UTC string ("YYYY-MM-DD HH:MM:SS")
      // which sorts correctly lexicographically. Conversations with no updated go to the bottom.
      state.conversations.sort((a: Conversation, b: Conversation) => {
        if (!a.updated && !b.updated) return 0;
        if (!a.updated) return 1;
        if (!b.updated) return -1;
        return b.updated > a.updated ? 1 : b.updated < a.updated ? -1 : 0;
      });

      // Re-resolve currConversationIndex after sort using stable address identity
      if (currAddr !== null && currAddrType !== null) {
        const newIdx = state.conversations.findIndex(
          (c: Conversation) => c.address === currAddr && c.addressType === currAddrType
        );
        if (newIdx !== -1) state.currConversationIndex = newIdx;
      }
    },
    // Matched by address rather than index: the caller resolves asynchronously, and a bump may
    // have re-sorted the list in the meantime.
    setConversationAddressId(
      state: ControlState,
      action: PayloadAction<{ address: string; addressId: number }>
    ) {
      const conv = state.conversations.find(
        (c: Conversation) => c.address === action.payload.address
      );
      if (conv) conv.addressId = action.payload.addressId;
    },
    updateConversationTime(state, action) {
      const index = action.payload.index;
      state.conversations[index].updated = action.payload.time;
    },
    // The global counter bumps on any key event (drives effect re-runs); the per-address
    // version bumps only when the event stored key material for that address, so the
    // retry gates ignore unrelated key traffic (see keyExchangeVersions in ControlState).
    keyExchangeReceived(state, action: PayloadAction<string | undefined>) {
      state.keyExchangeVersion += 1;
      const address = action.payload;
      if (address) {
        state.keyExchangeVersions[address] = (state.keyExchangeVersions[address] ?? 0) + 1;
      }
    },
    toggleEncryption(state) {
      state.encryptionEnabled = !state.encryptionEnabled;
    },
    toggleGroupEncryption(state) {
      state.groupEncryptionEnabled = !state.groupEncryptionEnabled;
    },
    setConversationEncryptionReady(state, action: PayloadAction<boolean>) {
      state.conversationEncryptionReady = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // For loadConversations
      .addCase(loadConversations.pending, (state: ControlState) => {
        state.loading = true;
        state.error = "";
      })
      .addCase(
        loadConversations.fulfilled,
        (state: ControlState, action: PayloadAction<Conversation[]>) => {
          // Re-resolve the open conversation by address after the list is replaced. This also
          // runs on socket reconnect with a conversation open, and the incoming list can be
          // ordered differently or a different length, so a bare index would silently move the
          // user into whatever thread now sits in that slot.
          const open =
            state.currConversationIndex !== null
              ? state.conversations[state.currConversationIndex]
              : undefined;

          state.conversations = action.payload.filter((c) => c.address !== KEYS_USERNAME);

          if (open) {
            const idx = state.conversations.findIndex(
              (c: Conversation) =>
                c.address === open.address && c.addressType === open.addressType
            );
            state.currConversationIndex = idx === -1 ? null : idx;
          }
          state.loading = false;
        }
      )
      .addCase(
        loadConversations.rejected,
        (
          state: ControlState,
          action: PayloadAction<unknown, string, never, SerializedError>
        ) => {
          state.loading = false;
          state.error = action.error.message || "Failed to load conversations";
        }
      )

      // Reset control state on logout so stale currConversationIndex from the
      // previous session can't crash the next user's conversation list render.
      .addCase("auth/logout/fulfilled", (state) => {
        state.conversations = [];
        state.currConversationIndex = null;
        state.loading = false;
        state.error = "";
        state.keyExchangeVersion = 0;
        state.keyExchangeVersions = {};
        state.encryptionEnabled = true;
        state.groupEncryptionEnabled = true;
        state.conversationEncryptionReady = false;
      })

      // For deleteConversation
      .addCase(deleteConversation.pending, (state) => {
        state.loading = true;
        state.error = "";
      })
      .addCase(deleteConversation.fulfilled, (state: ControlState, action) => {
        // Drop the row locally instead of re-fetching: ConversationList hides itself while
        // `loading` is true, so a GetConversations round-trip blanked the sidebar right after
        // the delete. Lands straight on the no-conversation-selected screen.
        state.conversations = state.conversations.filter(
          (c: Conversation) => c.address !== action.payload.address
        );
        state.currConversationIndex = null;
        state.conversationEncryptionReady = false;
        state.loading = false;
        state.error = "";
      })
      .addCase(deleteConversation.rejected, (state: ControlState, action) => {
        state.loading = false;
        // payload = the router's refusal message; action.error = a thrown network failure.
        state.error =
          action.payload || action.error.message || "Failed to delete conversation";
      });
  },
});

export const loadConversations = createAsyncThunk(
  "chat/loadConversations",
  async () => {
    const response: Conversation[] = await fetchConversations();
    return response;
  }
);

/**
 * Fills in the sm_addr id of a conversation started without one.
 *
 * An external address has no `sm_addr` row until the router handles the first send, so
 * NewConversationDialog opens the thread with `addressId: 0`. `GetConversationMessages` filters
 * on `s_to` / `r_from` — the id, never `with_addr` — so until this runs the thread reads empty.
 *
 * Deliberately narrower than `loadConversations()`, which replaces the whole array while
 * `currConversationIndex` keeps pointing at a position, silently re-targeting the open thread.
 */
export const resolveConversationAddressId = createAsyncThunk<void, string>(
  "chat/resolveConversationAddressId",
  async (address, { dispatch }) => {
    const conversations: Conversation[] = await fetchConversations();
    const match = conversations.find((c: Conversation) => c.address === address);
    if (match?.addressId) {
      dispatch(setConversationAddressId({ address, addressId: match.addressId }));
    }
  }
);

export const deleteConversation = createAsyncThunk<
  { address: string },
  string,
  { rejectValue: string }
>("chat/deleteConversation", async (address, { rejectWithValue }) => {
  const response = await deleteUserThread(address);
  // DeleteUserThread answers 200 with the outcome in the body, reporting success as
  // error: "Success" (GetGroupErrorStrings maps GRP_RC_OKAY to it). Anything else — "Not the
  // group owner", "Group does not exist", auth failure — is a refusal, not a completed delete.
  const error = response?.error;
  if (error && error !== "Success") return rejectWithValue(String(error));
  return { address };
});

export const {
  setCurrConversation,
  addConversation,
  setConversationAddressId,
  updateConversationTime,
  bumpConversation,
  moveCurrConversationToTop,
  keyExchangeReceived,
  markConversationUnread,
  toggleEncryption,
  toggleGroupEncryption,
  setConversationEncryptionReady,
} = controlSlice.actions;

export const selectConversations = (state: RootState) =>
  state.control.conversations;
export const selectCurrConversation = (state: RootState) =>
  state.control.currConversationIndex;
export const selectLoading = (state: RootState) => state.control.loading;
export const selectControlError = (state: RootState) => state.control.error;
export const selectKeyExchangeVersion = (state: RootState) => state.control.keyExchangeVersion;
export const selectKeyExchangeVersions = (state: RootState) => state.control.keyExchangeVersions;
export const selectEncryptionEnabled = (state: RootState) => state.control.encryptionEnabled;
export const selectGroupEncryptionEnabled = (state: RootState) => state.control.groupEncryptionEnabled;
export const selectConversationEncryptionReady = (state: RootState) => state.control.conversationEncryptionReady;

export default controlSlice.reducer;
